import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Eye, CheckCircle2, PhoneCall, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';

const PricesPage = () => {
  const { language, getLocalizedPath } = useLanguage();

  const isDa = language === 'da';

  const seoTitle = isDa 
    ? "Priser & Gennemsigtighed på Handicapvenlige Rejser" 
    : "Prices & Transparency on Accessible Holidays";

  const seoDescription = isDa
    ? "Få gennemsigtige priser på handicapvenlige rejser. Se hvad der er inkluderet i vores tryghedspakke, og hvordan vi sikrer en barrierefri ferie uden skjulte gebyrer."
    : "Get transparent prices on accessible holidays. See what is included in our safety package and how we ensure a barrier-free holiday with no hidden fees.";

  const heroTitle = isDa ? "Priser & Gennemsigtighed" : "Prices & Transparency";
  
  const heroNotice = isDa
    ? "Prisen er en fra-pris og afhænger blandt andet af afrejsedato, rejseperiode, værelsestype, ledige pladser og eventuelle særlige behov. Kontakt os for et konkret tilbud på din rejse."
    : "The price is a starting price and depends, among other things, on the departure date, travel period, room type, availability, and any special needs. Contact us for a specific quote for your trip.";

  const quoteText = isDa
    ? "“Hos Alora Rejser fjerner vi det tunge arbejde. Vi kender destinationerne, så du kan rejse med ro i maven.”"
    : "“At Alora Rejser, we take away the heavy lifting. We know the destinations, so you can travel with peace of mind.”";

  const badgeText = isDa ? "Vores prispolitik" : "Our pricing policy";
  const mainTitle = isDa ? "Hvad er inkluderet i din rejse?" : "What is included in your holiday?";

  const bulletPoints = isDa ? [
    {
      icon: ShieldCheck,
      title: "Ingen skjulte gebyrer",
      desc: "Vi oplyser prisen inklusiv de væsentlige elementer. Vi inkluderer altid nødvendig assistance og tilgængelige rammer, så du ikke får ubehagelige overraskelser."
    },
    {
      icon: Eye,
      title: "Tryghedspakken",
      desc: "En tryghedspakke med informationer om destinationen samt en guide til nærområdet inkl. akutnumre"
    },
    {
      icon: Heart,
      title: "Gennemtænkt kvalitet",
      desc: "Vi lægger vægt på tryghed, tilgængelighed og løsninger, der fungerer i praksis."
    }
  ] : [
    {
      icon: ShieldCheck,
      title: "No hidden fees",
      desc: "We state the price including the essential elements. We always include necessary assistance and accessible facilities, so you don't get any unpleasant surprises."
    },
    {
      icon: Eye,
      title: "The Safety Package",
      desc: "A safety package with information about the destination as well as a guide to the local area including emergency numbers."
    },
    {
      icon: Heart,
      title: "Thoughtful Quality",
      desc: "We emphasize safety, accessibility, and solutions that work in practice."
    }
  ];

  const qualityTitle = isDa ? "Kvalitet i alle led" : "Quality in every step";

  const qualityItems = isDa ? [
    "Flyrejse med lufthavnsassistance",
    "Håndplukkede tilgængelige hoteller",
    "Mulighed for privat lift-transport",
    "Dansk rejsegaranti og tryghed",
    "Akutnummer under rejsen"
  ] : [
    "Flight with airport assistance",
    "Handpicked accessible hotels",
    "Option for private lift-transport",
    "Danish travel guarantee and safety",
    "Emergency number during the trip"
  ];

  const qualityNotice = isDa
    ? "Bemærk: Da priserne på fly og hoteller varierer alt efter hvornår du ønsker at rejse, beregner vi altid den aktuelle dagspris til dig."
    : "Note: Since flight and hotel prices vary depending on when you wish to travel, we always calculate the current daily price for you.";

  const ctaTitle = isDa ? "Ønsker du et uforpligtende tilbud?" : "Would you like a non-binding quote?";
  const ctaDesc = isDa
    ? "Vi sidder klar til at beregne prisen på din næste ferie med ro i maven."
    : "We are ready to calculate the price of your next holiday with peace of mind.";

  const ctaButton1 = isDa ? "Udfyld tilbudsskema" : "Fill out quote form";
  const ctaButton2 = isDa ? "Ring til Alora" : "Call Alora";

  return (
    <div className="pt-24 md:pt-28 relative min-h-screen">
      <SEO 
        title={seoTitle} 
        description={seoDescription}
      />
      {/* Weak background image overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-15 pointer-events-none fixed"
        style={{
          backgroundImage: 'url("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg")',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      ></div>

      {/* Hero Section */}
      <section className="relative h-[30vh] md:h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[#99A18C]/80"></div>
        <div className="relative z-10 text-center text-white px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-6xl font-serif font-bold mb-4"
          >
            {heroTitle}
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-2xl text-xs md:text-sm max-w-2xl mx-auto font-light italic"
          >
            {heroNotice}
          </motion.div>
        </div>
      </section>

      {/* Intro Quote Section */}
      <section className="bg-[#99A18C]/5 py-12 border-b border-[#99A18C]/10 relative z-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-lg md:text-2xl font-serif italic text-slate-700 leading-relaxed">
            {quoteText}
          </p>
        </div>
      </section>

      {/* Why Transparency Matters */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="text-[#99A18C] font-bold uppercase tracking-widest text-sm mb-4 block">{badgeText}</span>
              <h2 className="text-2xl md:text-5xl font-serif font-bold text-slate-800 mb-8 leading-tight">
                {mainTitle}
              </h2>
              
              <div className="space-y-10">
                {bulletPoints.map((bullet, idx) => {
                  const IconComponent = bullet.icon;
                  return (
                    <div key={idx} className="flex gap-6">
                      <div className="flex-shrink-0 w-14 h-14 bg-[#99A18C]/10 rounded-2xl flex items-center justify-center text-[#99A18C]">
                        <IconComponent size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{bullet.title}</h3>
                        <p className="text-slate-600 leading-relaxed">
                          {bullet.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-[3rem] p-12 border border-[#99A18C]/10 shadow-xl shadow-[#99A18C]/5">
              <h3 className="text-2xl font-bold mb-6 text-slate-800">{qualityTitle}</h3>
              <ul className="space-y-4 mb-8">
                {qualityItems.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="text-[#99A18C] flex-shrink-0" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="p-6 bg-[#99A18C]/10 rounded-2xl border border-[#99A18C]/10">
                <p className="text-[#99A18C] text-sm font-medium">
                  {qualityNotice}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 relative z-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-serif font-bold mb-6">{ctaTitle}</h2>
          <p className="text-xl text-slate-600 mb-10">
            {ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to={getLocalizedPath('quote', language)} 
              className="flex items-center justify-center gap-3 bg-[#99A18C] text-white px-8 py-4 rounded-full font-bold hover:bg-[#8a917e] transition-all shadow-lg"
            >
              {ctaButton1}
            </Link>
            <a 
              href="tel:+4540500780" 
              className="flex items-center justify-center gap-3 bg-white text-[#99A18C] border-2 border-[#99A18C] px-8 py-4 rounded-full font-bold hover:bg-[#99A18C]/10 transition-all"
            >
              <PhoneCall size={20} />
              {ctaButton2}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricesPage;
